import fileDownload from "js-file-download";

/**
 * Helpers for viewing/downloading uploaded documents.
 *
 * Document files are served as static media by the API host (e.g.
 * https://teson.in/media/...), which is a different origin than the app and
 * sends no CORS headers - its preflight even answers 405 - so a request
 * carrying the Authorization header never reaches the file and a plain one
 * cannot be read as a blob. When that happens we hand the URL to the browser
 * instead, which is not subject to CORS.
 */

// A row is either the document itself (documents tables) or a link row that
// wraps it (attachments tables).
export const getDocument = (record) => record?.document || record;

export const getDocumentFileUrl = (record) => {
  const doc = getDocument(record);
  return doc?.is_signed && doc?.signed_document_file
    ? doc.signed_document_file
    : doc?.document_file;
};

export const getDocumentFileName = (record) => {
  const doc = getDocument(record);
  const name = doc?.name || "document";
  return doc?.is_signed && doc?.signed_document_file ? `signed_${name}` : name;
};

const openInBrowser = (url) => !!window.open(url, "_blank", "noopener,noreferrer");

const fetchBlob = async (url) => {
  const response = await fetch(url, { credentials: "omit" });
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
  return response.blob();
};

/**
 * Saves the file, falling back to opening it in a new tab when the browser
 * refuses to let us read it. Returns false only if that fallback was blocked
 * too (pop-up blocker), so the caller can tell the user.
 */
export const downloadDocumentFile = async (record) => {
  const url = getDocumentFileUrl(record);
  if (!url) return false;
  try {
    fileDownload(await fetchBlob(url), getDocumentFileName(record));
    return true;
  } catch (err) {
    console.log("Falling back to browser download: ", err);
    return openInBrowser(url);
  }
};

/**
 * Returns what PdfPreviewModal should render: the file as a blob when we are
 * allowed to read it, otherwise the URL itself (the modal shows those in an
 * iframe). Returns null when there is nothing to show.
 */
export const getDocumentPreviewSource = async (record) => {
  const url = getDocumentFileUrl(record);
  if (!url) return null;
  try {
    return await fetchBlob(url);
  } catch (err) {
    console.log("Falling back to URL preview: ", err);
    return url;
  }
};
